import { useNavigate } from "@tanstack/react-router";
import { LinkExternal01 } from "@untitledui/icons";
import type React from "react";
import { useEffect, useState } from "react";
import { useCheckSsoDomain } from "@/api/auth/check-sso-domain";
import { AlertBanner } from "@/components/alert-banner";
import { useLoginTransition } from "@/components/application/login-transition/login-transition";
import { AuthLinkButton, AuthSplitLayout, AuthTitle } from "@/components/auth/auth-split-layout";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { PasswordInput } from "@/components/base/input/password-input";
import { useRemoteConfig } from "@/hooks/use-remote-config";
import { authClient } from "@/lib/auth-client";
import { linkSso, signInWithSso, ssoErrorMessage } from "@/lib/sso";
import { useAuth } from "@/providers/auth-provider";
import { loginRoute } from "@/router";
import { safeRedirectPath } from "@/utils/safe-redirect";

export const Login = () => {
  // TODO: Form
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkedEmail, setCheckedEmail] = useState("");
  const [usePassword, setUsePassword] = useState(false);
  const [linking, setLinking] = useState(false);
  const navigate = useNavigate();
  const { onSignIn } = useAuth();
  const search = loginRoute.useSearch();
  const banner = useRemoteConfig("C-login-banner");
  const checkSsoDomain = useCheckSsoDomain(checkedEmail);
  const sso = checkedEmail.includes("@") ? (checkSsoDomain.data ?? null) : null;
  const lookUpSso = (email: string) => setCheckedEmail(email.toLowerCase().trim());

  const { start: startLoginTransition } = useLoginTransition();

  const completeSignIn = async () => {
    await startLoginTransition();
    await onSignIn();
    navigate({ to: safeRedirectPath(search.next) });
  };

  useEffect(() => {
    if (search.email && search.password) {
      navigate({ to: "/auto-login", search: { email: search.email, password: search.password } });
    } else if (search.email) {
      setUsername(search.email);
      lookUpSso(search.email);
    }
  }, [search.email, search.password, navigate]);

  useEffect(() => {
    if (!search.error) return;
    setError(ssoErrorMessage(search.error));
    if (search.error === "account_not_linked") {
      setLinking(true);
      setUsePassword(true);
    }
  }, [search.error]);

  const offerSso = !!sso?.sso && !(usePassword && !sso.enforced) && !linking;

  const signInErrorMessage = (status: number | undefined, message: string | undefined): string => {
    if (status === 0 || status === 503) return "Service temporarily unavailable. Please try again in a few minutes.";
    if (status === 401 || status === 403) return "Log in failed. Please check your credentials.";
    return message || "Log in failed";
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const email = username.toLowerCase().trim();
    try {
      if (offerSso) {
        const ssoError = await signInWithSso(email, safeRedirectPath(search.next));
        if (ssoError) setError(ssoErrorMessage(ssoError.code ?? ""));
        return;
      }
      const { data, error: signInError } = await authClient.signIn.email({ email, password });
      if (data && "twoFactorRedirect" in data && data.twoFactorRedirect) {
        navigate({ to: "/login/two-factor", search: { next: search.next, ...(linking ? { link: email } : {}) } });
        return;
      }
      if (signInError) {
        setError(signInErrorMessage(signInError.status, signInError.message));
        return;
      }
      if (linking) {
        const linkError = await linkSso(email, safeRedirectPath(search.next));
        if (linkError) setError(ssoErrorMessage(linkError.code ?? ""));
        return;
      }
      await completeSignIn();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitLayout
      banner={
        banner && (
          <AlertBanner>
            {banner.title && <span className="font-semibold">{banner.title} </span>}
            {banner.message}
          </AlertBanner>
        )
      }
    >
      <form onSubmit={handleSignIn} className="flex w-full max-w-[350px] flex-col">
        <AuthTitle>Log in to Unbox</AuthTitle>
        <div className="mt-7 flex flex-col gap-4">
          <Input
            label="Email"
            type="email"
            placeholder="example@email.com"
            value={username}
            onChange={setUsername}
            onBlur={() => lookUpSso(username)}
            isRequired
            hideRequiredIndicator
            isDisabled={loading}
            autoComplete="username"
          />
          {!offerSso && (
            <PasswordInput
              label="Password"
              value={password}
              onChange={setPassword}
              isRequired
              hideRequiredIndicator
              isDisabled={loading}
              autoComplete="current-password"
            />
          )}
        </div>
        {error && (
          <p role="alert" className="mt-4 text-sm text-error-600">
            {error}
          </p>
        )}
        <Button type="submit" size="lg" className="mt-6 w-full" isLoading={loading}>
          {offerSso ? "Continue with single sign-on" : "Log in"}
        </Button>
        {sso?.sso && !sso.enforced && !linking && (
          <AuthLinkButton className="mt-5" onClick={() => setUsePassword(!usePassword)} isDisabled={loading}>
            {usePassword ? "Use single sign-on instead" : "Use a password instead"}
          </AuthLinkButton>
        )}
        {!offerSso && (
          <AuthLinkButton
            className="mt-5"
            onClick={() => navigate({ to: "/auth/forgot-password" })}
            isDisabled={loading}
          >
            Forgot Password?
          </AuthLinkButton>
        )}
        <a
          href="https://status.unbox.social"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed right-8 bottom-8 inline-flex items-center gap-1 text-[13px] font-medium text-gray-500 hover:text-gray-700"
        >
          System status
          <LinkExternal01 className="size-3.5" />
        </a>
      </form>
    </AuthSplitLayout>
  );
};
